import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Admin/Sidebar";
import Header from "../../components/Admin/Header";
import styles from "./Affiliate.module.css";
import api from "../../api/axois";

// Circular countdown component
const CircularCountdown = ({ daysUntilPayment, isOverdue, payoutFrequency }) => {
  const maxDays = payoutFrequency === 'weekly' ? 7 : 30;
  const percentage = isOverdue ? 0 : Math.max(0, (daysUntilPayment / maxDays) * 100);
  const radius = 20;
  const strokeWidth = 4;
  const normalizedRadius = radius - strokeWidth * 2;
  const circumference = normalizedRadius * 2 * Math.PI;
  const strokeDasharray = `${circumference} ${circumference}`;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;
  
  let color = '#28a745'; // green
  if (isOverdue) color = '#dc3545'; // red
  else if (daysUntilPayment <= 1) color = '#ffc107'; // yellow
  else if (daysUntilPayment <= 3) color = '#fd7e14'; // orange

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <svg width={radius * 2} height={radius * 2} style={{ transform: 'rotate(-90deg)' }}>
        <circle
          stroke="#e9ecef"
          fill="transparent"
          strokeWidth={strokeWidth}
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
        <circle
          stroke={color}
          fill="transparent"
          strokeWidth={strokeWidth}
          strokeDasharray={strokeDasharray}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          r={normalizedRadius}
          cx={radius}
          cy={radius}
        />
      </svg>
      <div style={{ fontSize: 12, fontWeight: 'bold', color }}>
        {isOverdue ? 'OVERDUE!' : daysUntilPayment === 0 ? 'TODAY' : `${Math.floor(daysUntilPayment)}d`}
      </div>
    </div>
  );
};

const Affiliate = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [affiliates, setAffiliates] = useState([]);
  const [payAmounts, setPayAmounts] = useState({}); // { [affiliateId]: amount }
  const [deleteConfirm, setDeleteConfirm] = useState(null); // affiliateId pending delete

  const navigate = useNavigate();

  useEffect(() => {
    const loadAll = async () => {
      setLoading(true);
      setError("");
      try {
        const res = await api.get("/affiliate/admin/all");
        setAffiliates(res.data.affiliates || []);
      } catch (e) {
        setError(e.response?.data?.message || "Failed to load affiliates");
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, []);

  const reload = async () => {
    try {
      const res = await api.get("/affiliate/admin/all");
      setAffiliates(res.data.affiliates || []);
    } catch {}
  };

  const handlePay = async (affiliateId) => {
    const amountRaw = payAmounts[affiliateId];
    const amt = Number(amountRaw);
    if (!amt || amt <= 0) return;
    setError("");
    setSuccess("");
    try {
      await api.post("/affiliate/admin/pay", { affiliate_id: affiliateId, amount: amt });
      setSuccess("Payout recorded as paid.");
      setPayAmounts((prev) => ({ ...prev, [affiliateId]: "" }));
      await reload();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || "Failed to pay affiliate");
    }
  };

  const handleMarkRequestedPaid = async (affiliateId) => {
    setError("");
    setSuccess("");
    try {
      await api.post("/affiliate/admin/mark-all-requested-paid", { affiliate_id: affiliateId });
      setSuccess("All requested payouts marked as paid.");
      await reload();
    } catch (e) {
      setError(e.response?.data?.error || e.response?.data?.message || "Failed to mark requested payouts as paid");
    }
  };

  const handleDelete = async (affiliateId) => {
    setError("");
    setSuccess("");
    try {
      await api.delete(`/affiliate/admin/${affiliateId}`);
      setSuccess("Affiliate deleted successfully.");
      setDeleteConfirm(null);
      await reload();
    } catch (e) {
      setError(e.response?.data?.message || "Failed to delete affiliate");
    }
  };

  const getPaymentCountdownDisplay = (affiliate) => {
    if (!affiliate) return <div>-</div>;
    
    return (
      <CircularCountdown 
        daysUntilPayment={affiliate.days_until_payment || 0}
        isOverdue={affiliate.is_payment_overdue || false}
        payoutFrequency={affiliate.payout_frequency || 'monthly'}
      />
    );
  };

  return (
    <div className={styles.container}>
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <div className={styles.content}>
        <Header onMenuClick={() => setSidebarOpen(true)} />

        <div className={styles.wrapper}>
          <h2 className={styles.title}>Affiliates</h2>
          {loading && <div>Loading...</div>}
          {error && <div className={styles.error}>{error}</div>}
          {success && <div className={styles.success}>{success}</div>}

          <div className={styles.tableWrapper}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>Affiliate</th>
                  <th>Code</th>
                  <th>Pending (₦)</th>
                  <th>Paid Total (₦)</th>
                  <th>Payment Countdown</th>
                  <th>Bank</th>
                  <th>Account</th>
                  <th>Pay</th>
                  <th>Delete</th>
                </tr>
              </thead>
              <tbody>
                {affiliates.length === 0 ? (
                  <tr>
                    <td colSpan="9" className={styles.noData}>No affiliates found.</td>
                  </tr>
                ) : (
                  affiliates.map((a) => (
                    <tr key={a.id} style={{ 
                      backgroundColor: a.is_payment_overdue ? '#fff2f2' : 
                                      a.days_until_payment <= 1 ? '#fff8dc' : 'transparent' 
                    }}>
                      <td>
                        <div style={{display:'flex', flexDirection:'column'}}>
                          <span>{a.user?.name || '-'}</span>
                          <small style={{opacity:0.8}}>{a.user?.email || ''}</small>
                        </div>
                      </td>
                      <td>{a.affiliate_code}</td>
                      <td>₦{Number(a.pending_commission || 0).toLocaleString()}</td>
                      <td>₦{Number(a.total_paid || 0).toLocaleString()}</td>
                      <td>{getPaymentCountdownDisplay(a)}</td>
                      <td>{a.bank_name || '-'}</td>
                      <td>{a.account_number || '-'}</td>
                      <td>
                        <div className={styles.payoutRow}>
                          <input
                            className={styles.input}
                            type="number"
                            min="0"
                            placeholder="Amount"
                            value={payAmounts[a.id] ?? ""}
                            onChange={(e) => setPayAmounts((prev) => ({ ...prev, [a.id]: e.target.value }))}
                            style={{maxWidth:120}}
                          />
                          <button className={styles.requestBtn} onClick={() => handlePay(a.id)}>Pay</button>
                        </div>
                        <button 
                          className={styles.requestBtn} 
                          style={{marginTop:6, fontSize: 12}} 
                          onClick={() => {
                            setPayAmounts(prev => ({ ...prev, [a.id]: String(a.pending_commission || 0) }));
                          }}
                        >
                          Use Pending Total
                        </button>
                      </td>
                      <td>
                        {deleteConfirm === a.id ? (
                          <div style={{display:'flex', gap:4, alignItems:'center'}}>
                            <span style={{fontSize:11, color:'#dc3545'}}>Sure?</span>
                            <button
                              className={styles.requestBtn}
                              style={{background:'#dc3545', borderColor:'#dc3545', fontSize:11, padding:'3px 8px'}}
                              onClick={() => handleDelete(a.id)}
                            >
                              Yes
                            </button>
                            <button
                              className={styles.requestBtn}
                              style={{fontSize:11, padding:'3px 8px'}}
                              onClick={() => setDeleteConfirm(null)}
                            >
                              No
                            </button>
                          </div>
                        ) : (
                          <button
                            className={styles.requestBtn}
                            style={{background:'#dc3545', borderColor:'#dc3545'}}
                            onClick={() => setDeleteConfirm(a.id)}
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {sidebarOpen && <div className={styles.overlay} onClick={() => setSidebarOpen(false)} />}
    </div>
  );
};

export default Affiliate;
